import {
  Box,
  Button,
  Checkbox,
  Divider,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ReactNode, useState } from 'react'
import { Loader2, Telescope } from 'lucide-react'

export type DiscoverLayers = 1 | 2 | 3

export interface DiscoverOptions {
  layers: DiscoverLayers
  // Concise search lanes (backend keys). Empty/all = unrestricted.
  categories?: string[]
}

// What to search for. Keys match the backend's DISCOVERY_CATEGORIES map.
const CATEGORY_OPTIONS: { key: string; label: string; blurb: string }[] = [
  // Social media is handled by the dedicated social-search tools, not web
  // discovery — so it's intentionally not a discovery lane here.
  { key: 'marketplaces', label: 'Marketplaces', blurb: 'mirror storefronts & wholesale' },
  { key: 'websites', label: 'Online sites', blurb: 'related domains, backlinks & co-hosting' },
  { key: 'news', label: 'News & reviews', blurb: 'articles, forums & complaints' },
  { key: 'records', label: 'Company records', blurb: 'registries, WHOIS & enforcement' },
]

// Each layer runs 5 queries. Layer 1 starts broad from the entity itself;
// every further layer pivots on the top-graded matches from the previous one,
// forming deeper queries connected to what was just discovered.
const LAYER_OPTIONS: DiscoverLayers[] = [1, 2, 3]

const LAYER_META: Record<DiscoverLayers, { eta: string; blurb: string }> = {
  1: { eta: '~30s', blurb: '5 queries · broad first pass' },
  2: { eta: '~75s', blurb: '10 queries · 1 deeper pivot' },
  3: { eta: '~2m',  blurb: '15 queries · 2 deeper pivots' },
}

interface DiscoverWebPopoverProps {
  trigger: ReactNode
  isRunning: boolean
  onStart: (opts: DiscoverOptions) => void
  isDisabled?: boolean
}

const DiscoverWebPopover = ({
  trigger,
  isRunning,
  onStart,
  isDisabled,
}: DiscoverWebPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [layers, setLayers] = useState<DiscoverLayers>(1)
  const [categories, setCategories] = useState<Set<string>>(
    () => new Set(CATEGORY_OPTIONS.map((c) => c.key)),
  )

  const toggleCategory = (key: string) => {
    setCategories((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        if (next.size === 1) return prev // keep at least one lane selected
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleStart = () => {
    if (isRunning || isDisabled) return
    const allSelected = categories.size === CATEGORY_OPTIONS.length
    onStart({ layers, categories: allSelected ? undefined : Array.from(categories) })
    setIsOpen(false)
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => !isDisabled && !isRunning && setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement="top"
      closeOnBlur
      isLazy
    >
      <PopoverTrigger>{trigger as any}</PopoverTrigger>
      <PopoverContent
        w="300px"
        bg="white"
        borderRadius="12px"
        border="1px solid"
        borderColor="neutral.100"
        boxShadow="0 16px 40px -12px rgba(15, 23, 42, 0.18), 0 2px 6px -2px rgba(15, 23, 42, 0.06)"
        _focus={{ outline: 'none', boxShadow: '0 16px 40px -12px rgba(15, 23, 42, 0.18)' }}
        zIndex={2147483647}
      >
        <PopoverArrow />
        <PopoverBody p="14px">
          {/* Layers */}
          <Text fontSize="10px" fontWeight={700} color="neutral.500" textTransform="uppercase" letterSpacing="0.08em" mb="6px">
            Layers
          </Text>
          <HStack
            spacing="4px"
            bg="neutral.50"
            p="3px"
            borderRadius="10px"
            mb="10px"
          >
            {LAYER_OPTIONS.map((n) => {
              const active = layers === n
              return (
                <Box
                  key={n}
                  as="button"
                  flex={1}
                  py="7px"
                  borderRadius="8px"
                  fontSize="13px"
                  fontWeight={active ? 700 : 500}
                  color={active ? 'neutral.900' : 'neutral.500'}
                  bg={active ? 'white' : 'transparent'}
                  boxShadow={active ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)' : 'none'}
                  transition="all 0.15s ease-out"
                  onClick={() => setLayers(n)}
                >
                  {n}
                  <Text fontSize="9px" color="neutral.400" mt="1px" fontWeight={500}>
                    {LAYER_META[n].eta}
                  </Text>
                </Box>
              )
            })}
          </HStack>
          <Text fontSize="11px" color="neutral.500" lineHeight="1.4" mb="4px">
            {LAYER_META[layers].blurb}
          </Text>
          <Text fontSize="10px" color="neutral.400" lineHeight="1.4">
            Each layer runs 5 queries. Later layers go deeper, building new
            queries from the top-graded results of the previous layer.
          </Text>

          <Divider my="12px" />

          {/* Search lanes */}
          <Text fontSize="10px" fontWeight={700} color="neutral.500" textTransform="uppercase" letterSpacing="0.08em" mb="6px">
            Search for
          </Text>
          <VStack align="stretch" spacing="1px">
            {CATEGORY_OPTIONS.map((c) => {
              const checked = categories.has(c.key)
              return (
                <Checkbox
                  key={c.key}
                  size="sm"
                  colorScheme="purple"
                  isChecked={checked}
                  onChange={() => toggleCategory(c.key)}
                  alignItems="flex-start"
                  spacing="10px"
                  px="8px"
                  py="7px"
                  borderRadius="8px"
                  transition="background 0.12s ease-out"
                  _hover={{ bg: 'neutral.50' }}
                  sx={{ '.chakra-checkbox__control': { mt: '2px' } }}
                >
                  <Box>
                    <Text fontSize="12.5px" fontWeight={600} color="neutral.800" lineHeight="1.3">
                      {c.label}
                    </Text>
                    <Text fontSize="10.5px" color="neutral.400" lineHeight="1.35" mt="1px">
                      {c.blurb}
                    </Text>
                  </Box>
                </Checkbox>
              )
            })}
          </VStack>

          <Divider my="12px" />

          <Button
            w="100%"
            size="sm"
            colorScheme="purple"
            isDisabled={isRunning}
            leftIcon={
              isRunning ? (
                <Box
                  as={Loader2}
                  w="14px"
                  h="14px"
                  sx={{
                    '@keyframes discover-popover-spin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                    animation: 'discover-popover-spin 0.9s linear infinite',
                  }}
                />
              ) : (
                <Box as={Telescope} w="14px" h="14px" />
              )
            }
            onClick={handleStart}
          >
            {isRunning
              ? 'Investigating…'
              : `Investigate · ${layers} ${layers === 1 ? 'layer' : 'layers'}`}
          </Button>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default DiscoverWebPopover
